import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Accordion } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import AdminSidebar from "./Components/AdminSidebar";
import AdminHeader from "./Components/AdminHeader";
import CustomButton from "../../Components/Button";
import AddressInput from "../../Components/AddressInput";
import PasswordInput from "../../Components/PasswordInput";
import UserMenuSidebar from "../User/Components/MenuSidebar";

import { defaultUserImg } from "../../Constants/Images";
import { isValidEmail, showToast } from "../../utils/helpers";
import {
  changePasswordAction,
  updateUserAction,
} from "../../Services/Store/Actions/AuthActions";
import PanelWrapper from "../User/PanelWrapper";

interface UserProfileType {
  name: string;
  image: string;
  email: string;
  phone: string;
  dob: string;
  NID_PASSPORT: string;
  address: string;
  state: string;
  city: string;
  zipcode: string;
  businessName: string;
  description: string;
  country: string;
}

const defUserInfo = {
  name: "",
  image: "",
  email: "",
  phone: "",
  dob: "",
  NID_PASSPORT: "",
  address: "",
  state: "",
  city: "",
  zipcode: "",
  businessName: "",
  description: "",
  country: "",
};

const ProfilePage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: any) => state.auth);
  // This page is shared by /admin/profile and /user/profile, so pick the
  // sidebar based on the current route rather than hardcoding it.
  const { pathname } = useLocation();
  const panelType: "admin" | "user" = pathname.startsWith("/admin")
    ? "admin"
    : "user";
  const [data, setData] = useState<UserProfileType>({ ...defUserInfo });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    dob: "",
    passport: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zipcode: "",
    currentPassword: "",
    password: "",
    cpassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [cpassword, setCPassword] = useState("");

  const [newProfileImg, setNewProfileImg] = useState(null);
  const [profileImg, setProfileImg] = useState(defaultUserImg);

  const onChange = (field: string, value: string) => {
    setData({
      ...data,
      [field]: value,
    });
  };

  const updateChooserImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const imgFile = e && e.target && e.target.files ? e.target.files[0] : null;

    console.log("imgFile :", imgFile);

    if (String(imgFile?.type).includes("image")) {
      setNewProfileImg(imgFile);
      const imgUrl = URL.createObjectURL(imgFile);

      setProfileImg(imgUrl);
    } else {
      showToast("error", "Only Images are allowed");
    }
  };

  const validate = () => {
    let isErrs = false;
    const errs = {
      name: "",
      email: "",
      phone: "",
      dob: "",
      passport: "",
      address: "",
      city: "",
      state: "",
      country: "",
      zipcode: "",
      currentPassword: "",
      password: "",
      cpassword: "",
    };

    if (!data.name || data.name === "") {
      isErrs = true;
      errs.name = "Please enter your name";
    }

    // if (!data.dob || data.dob === "") {
    //   isErrs = true;
    //   errs.dob = "Please enter your Birthdate";
    // }

    // if (!data.NID_PASSPORT || data.NID_PASSPORT === "") {
    //   isErrs = true;
    //   errs.passport = "Please enter your name";
    // }

    if (!data.email || data.email === "") {
      isErrs = true;
      errs.email = "Please enter your email";
    } else if (!isValidEmail(data.email)) {
      isErrs = true;
      errs.email = "Please enter valid email";
    }

    setErrors(errs);

    return isErrs;
  };

  const saveUserProfile = () => {
    const isErr = validate();

    if (!loading && !isErr) {
      //
      setLoading(true);

      const formData = new FormData();

      // Append as a clean string, never the literal "null"/"undefined".
      // FormData coerces a null/undefined value to the string "null", which is
      // how empty fields ended up stored as the text "null" in the DB.
      const appendField = (key: string, value: any) => {
        const safe =
          value === null ||
          value === undefined ||
          value === "null" ||
          value === "undefined"
            ? ""
            : value;
        formData.append(key, safe);
      };

      appendField("name", data.name);
      appendField("phone", data.phone);
      appendField("dob", data.dob);
      appendField("NID_PASSPORT", data.NID_PASSPORT);
      appendField("country", data.country);
      appendField("state", data.state);
      appendField("city", data.city);
      appendField("address", data.address);
      appendField("zipcode", data.zipcode);

      if (newProfileImg) {
        formData.append("image", newProfileImg);
      }

      updateUserAction(formData, dispatch)
        .then((res) => {
          showToast(res.type, res.message);
        })
        .catch((err) => {
          showToast("error", err.message);
        })
        .finally(() => setLoading(false));
    }
  };

  // Changing the password is a separate, security-sensitive flow: it requires
  // the current password and posts to a dedicated endpoint that verifies it
  // server-side. It must not ride along with the profile (multipart) save.
  const changeUserPassword = () => {
    const errs = { ...errors, currentPassword: "", password: "", cpassword: "" };
    let isErr = false;

    if (currentPassword === "") {
      errs.currentPassword = "Please enter your current password.";
      isErr = true;
    }

    if (password === "") {
      errs.password = "Please enter a new password.";
      isErr = true;
    } else if (String(password).length < 6) {
      errs.password = "Your password must be at least 6 characters long.";
      isErr = true;
    }

    if (!isErr && cpassword === "") {
      errs.cpassword = "Please confirm your new password.";
      isErr = true;
    } else if (!isErr && password !== cpassword) {
      errs.cpassword = "The passwords you entered don't match.";
      isErr = true;
    }

    if (!isErr && currentPassword === password) {
      errs.password =
        "Your new password must be different from your current password.";
      isErr = true;
    }

    setErrors(errs);

    if (isErr || passwordLoading) {
      return;
    }

    setPasswordLoading(true);

    changePasswordAction({ currentPassword, newPassword: password })
      .then((res) => {
        showToast(res.type, res.message);
        if (res.type === "success") {
          setCurrentPassword("");
          setPassword("");
          setCPassword("");
        }
      })
      .catch((err) => {
        showToast("error", err.message);
      })
      .finally(() => setPasswordLoading(false));
  };

  useEffect(() => {
    if (user) {
      // The API returns null for empty optional fields. Merge over the defaults
      // and replace any null/undefined with "" so inputs don't render the
      // literal text "null". Also treat the strings "null"/"undefined" as empty
      // — earlier saves wrote those literal strings to the DB (FormData coerces
      // a null value to the string "null").
      const normalized: any = { ...defUserInfo };
      Object.keys(defUserInfo).forEach((key) => {
        const value = (user as any)[key];
        const isEmpty =
          value === null ||
          value === undefined ||
          value === "null" ||
          value === "undefined";
        normalized[key] = isEmpty ? "" : value;
      });
      setData(normalized);

      if (user.image) {
        setProfileImg(user.image);
      }
    }
  }, [user]);

  console.log("Data :", data);

  return (
    <PanelWrapper type={panelType}>
      <div className="container">
        <div className="page-header">
          <h2>Profile</h2>
        </div>

        <div className="page-content-card">
          <div className="profile-img-wrapper">
            <div className="profile-img">
              <input
                type="file"
                className="prf-img"
                onChange={updateChooserImage}
              />

              <img src={profileImg} alt="" />

              <span className="plus-icon">
                <FontAwesomeIcon icon={faPlus} />
              </span>
            </div>
          </div>

          <div className="profile-form-wrapper">
            <Accordion alwaysOpen defaultActiveKey={["0", "1"]}>
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <div className="d-flex w-100 align-item-center justify-content-between">
                    <h3 className="m-0">Personal Information</h3>

                    <span className="dots-icon">
                      <FontAwesomeIcon icon={faChevronDown} />
                    </span>
                  </div>
                </Accordion.Header>

                <Accordion.Body>
                  <p className="required-note">
                    <span className="required-star">*</span> Required
                  </p>
                  <div className="row">
                    <div className="col-md-6 col-sm-6 col-xs-12">
                      <div className="form-group type-2">
                        <label>
                          Full Name<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          placeholder="Your Name"
                          className="form-control"
                          value={data.name}
                          onChange={(e) => onChange("name", e.target.value)}
                        />

                        {errors.name && (
                          <span className="error">{errors.name}</span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 col-sm-6 col-xs-12">
                      <div className="form-group type-2">
                        <label>
                          Email<span className="required-star">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          placeholder="Your Email"
                          className="form-control"
                          value={data.email}
                          onChange={(e) => onChange("email", e.target.value)}
                        />

                        {errors.email && (
                          <span className="error">{errors.email}</span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 col-sm-6 col-xs-12">
                      <div className="form-group type-2">
                        <label>Phone No</label>
                        <input
                          type="text"
                          name="phone"
                          placeholder="e.g. +1 555 123 4567"
                          className="form-control"
                          value={data.phone}
                          onChange={(e) => onChange("phone", e.target.value)}
                        />

                        {errors.phone && (
                          <span className="error">{errors.phone}</span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 col-sm-6 col-xs-12">
                      <div className="form-group type-2">
                        <label>Date of Birth</label>
                        <input
                          type="date"
                          name="phone"
                          placeholder="Your DOB"
                          className="form-control"
                          value={data.dob}
                          onChange={(e) => onChange("dob", e.target.value)}
                        />
                        {errors.dob && (
                          <span className="error">{errors.dob}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    {/* <div className="col-md-4 col-sm-4 col-xs-12">
                          <div className="form-group type-2">
                            <label>NID / Passport*</label>
                            <input
                              type="text"
                              name="passport"
                              placeholder="Your NID / Passport"
                              className="form-control"
                              value={data.NID_PASSPORT}
                              onChange={(e) =>
                                onChange("NID_PASSPORT", e.target.value)
                              }
                            />
                            {errors.passport && (
                              <span className="error">{errors.passport}</span>
                            )}
                          </div>
                        </div> */}

                    <div className="col-md-8 col-sm-8 col-xs-12">
                      <div className="form-group type-2">
                        <label>Address</label>

                        <AddressInput
                          type="fullObject"
                          address={data.address}
                          onChange={(address: object) => {
                            console.log("address :", address);
                            if (typeof address === "object") {
                              setData({
                                ...data,
                                ...address,
                                zipcode: address.postalCode,
                              });
                            } else {
                              onChange("address", address);
                            }
                          }}
                        />
                        {/* <input
                              type="text"
                              name="address"
                              placeholder="Your Address"
                              className="form-control"
                              value={data.address}
                              onChange={(e) =>
                                onChange("address", e.target.value)
                              }
                            /> */}
                        {errors.address && (
                          <span className="error">{errors.address}</span>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4 col-sm-4 col-xs-12">
                      <div className="form-group type-2">
                        <label>City</label>
                        <input
                          type="text"
                          name="city"
                          placeholder="Your City"
                          className="form-control"
                          value={data.city}
                          onChange={(e) => onChange("city", e.target.value)}
                        />
                        {errors.city && (
                          <span className="error">{errors.city}</span>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4 col-sm-4 col-xs-12">
                      <div className="form-group type-2">
                        <label>State</label>
                        <input
                          type="text"
                          name="state"
                          placeholder="Your State"
                          className="form-control"
                          value={data.state}
                          onChange={(e) => onChange("state", e.target.value)}
                        />
                        {errors.state && (
                          <span className="error">{errors.state}</span>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4 col-sm-4 col-xs-12">
                      <div className="form-group type-2">
                        <label>Country</label>
                        <input
                          type="text"
                          name="country"
                          placeholder="Your Country"
                          className="form-control"
                          value={data.country}
                          onChange={(e) => onChange("country", e.target.value)}
                        />
                        {errors.country && (
                          <span className="error">{errors.country}</span>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4 col-sm-4 col-xs-12">
                      <div className="form-group type-2 mb-0">
                        <label>Zip code</label>
                        <input
                          type="text"
                          name="state"
                          placeholder="e.g. 90210"
                          className="form-control"
                          value={data.zipcode}
                          onChange={(e) => onChange("zipcode", e.target.value)}
                        />
                        {errors.zipcode && (
                          <span className="error">{errors.zipcode}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <div className="d-flex w-100 align-item-center justify-content-between">
                    <h3 className="m-0">Change Password</h3>

                    <span className="dots-icon">
                      <FontAwesomeIcon icon={faChevronDown} />
                    </span>
                  </div>
                </Accordion.Header>

                <Accordion.Body>
                  <div className="row">
                    <div className="col-md-12 col-sm-12 col-xs-12">
                      <div className="form-group type-2">
                        <label>Current Password</label>

                        <PasswordInput
                          name="currentPassword"
                          placeholder="Current Password"
                          value={currentPassword}
                          onChange={(val) => {
                            setCurrentPassword(val);
                            setErrors((prev) => ({
                              ...prev,
                              currentPassword: "",
                            }));
                          }}
                        />
                        {errors.currentPassword && (
                          <span className="error">
                            {errors.currentPassword}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6 col-sm-6 col-xs-12">
                      <div className="form-group type-2">
                        <label>New Password</label>

                        <PasswordInput
                          name="password"
                          placeholder="New Password"
                          value={password}
                          onChange={(val) => {
                            setPassword(val);
                            setErrors((prev) => ({ ...prev, password: "" }));
                          }}
                        />
                        {errors.password && (
                          <span className="error">{errors.password}</span>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6 col-sm-6 col-xs-12">
                      <div className="form-group type-2">
                        <label>Confirm New Password</label>

                        <PasswordInput
                          name="confirmPassword"
                          placeholder="Confirm New Password"
                          value={cpassword}
                          onChange={(val) => {
                            setCPassword(val);
                            setErrors((prev) => ({ ...prev, cpassword: "" }));
                          }}
                        />
                        {errors.cpassword && (
                          <span className="error">{errors.cpassword}</span>
                        )}
                      </div>
                    </div>

                    <div className="col-md-12 col-sm-12 col-xs-12 d-flex justify-content-end">
                      <CustomButton
                        classList="btn-sm"
                        isLoading={passwordLoading}
                        title="Change Password"
                        onClick={() => changeUserPassword()}
                      />
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            <CustomButton
              isLoading={loading}
              title="Update Profile"
              onClick={() => saveUserProfile()}
            />
          </div>
        </div>
      </div>
    </PanelWrapper>
  );
};

export default ProfilePage;
